import { useState } from "react";
import { Button } from "./ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, ShoppingCart, Target, History, Eye, Phone, Package, BarChart3 } from "lucide-react";
// import logoImage from 'nx-procure-ai/Assets/Frame 3434@2x.png';
import logoImage from '../Assets/logo.png';




interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export function Navigation({ currentPage, onPageChange }: NavigationProps) {
  const [isOpen, setIsOpen] = useState(false);

  const aboutItems = [
    { label: "Our Story", value: "story", icon: History },
    { label: "Mission", value: "mission", icon: Target },
    { label: "Vision", value: "vision", icon: Eye },
  ];

  const solutionItems = [
    {
      label: "ProcPay",
      value: "procpay",
      icon: ShoppingCart,
      description: "Full P2P automation"
    },
    {
      label: "ProLiMa",
      value: "plm",
      icon: Package,
      description: "Product lifecycle management"
    },
    {
      label: "NexAI",
      value: "nexai",
      icon: BarChart3,
      description: "AI insights, anomaly detection"
    },
    {
      label: "Demand Forecasting",
      value: "demand-forecasting",
      icon: BarChart3,
      description: "Predictive stock intelligence"
    }
  ];

  // Helper function to determine which top-level menu should be active
  const getActiveMenu = (page: string) => {
    // About Us pages
    if (["story", "mission", "vision"].includes(page)) {
      return "about";
    }
    
    // Solution pages - main solution pages only
    if (["procpay", "plm", "nexai", "demand-forecasting"].includes(page)) {
      return "solutions";
    }
    
    // Contact page
    if (page === "contact") {
      return "contact";
    }
    
    // Default to home
    return "home";
  };

  const activeMenu = getActiveMenu(currentPage);

  const handleNavigation = (page: string) => {
    onPageChange(page);
    setIsOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between max-w-none">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => handleNavigation('home')}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={logoImage} 
                alt="NexProcureAI Logo"
                className="h-12 w-auto"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {/* About Us Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className="transition-colors data-[state=open]:text-white"
                  style={{
                    backgroundColor: activeMenu === 'about' ? '#819A40' : 'white',
                    color: activeMenu === 'about' ? 'white' : 'black'
                  }}
                  onMouseEnter={(e) => {
                    if (activeMenu !== 'about') {
                      e.currentTarget.style.backgroundColor = '#819A40';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.getAttribute('data-state') && activeMenu !== 'about') {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = 'black';
                    }
                  }}
                >
                  About Us
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-white">
                    {aboutItems.map((item) => (
                      <li key={item.value}>
                        <NavigationMenuLink asChild>
                          <button
                            onClick={() => handleNavigation(item.value)}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:text-white focus:text-white w-full text-left text-black"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#819A40';
                              e.currentTarget.style.color = 'white';
                              const icon = e.currentTarget.querySelector('.nav-icon') as HTMLElement;
                              if (icon) icon.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                              e.currentTarget.style.color = 'black';
                              const icon = e.currentTarget.querySelector('.nav-icon') as HTMLElement;
                              if (icon) icon.style.color = '#819A40';
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <item.icon className="h-4 w-4 nav-icon" style={{ color: '#819A40' }} />
                              <div className="text-sm font-medium leading-none">{item.label}</div>
                            </div>
                          </button>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Solutions Menu */}
              <NavigationMenuItem>
                <NavigationMenuTrigger 
                  className="transition-colors data-[state=open]:text-white"
                  style={{
                    backgroundColor: activeMenu === 'solutions' ? '#819A40' : 'white',
                    color: activeMenu === 'solutions' ? 'white' : 'black'
                  }}
                  onMouseEnter={(e) => {
                    if (activeMenu !== 'solutions') {
                      e.currentTarget.style.backgroundColor = '#819A40';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.getAttribute('data-state') && activeMenu !== 'solutions') {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.color = 'black';
                    }
                  }}
                >
                  Solutions
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[600px] gap-3 p-4 lg:w-[800px] lg:grid-cols-2 bg-white">
                    {solutionItems.map((item) => (
                      <li key={item.value} className="row-span-3">
                        <button
                          onClick={() => handleNavigation(item.value)}
                          className="w-full text-left block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:text-white focus:text-white text-black"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#819A40';
                            e.currentTarget.style.color = 'white';
                            const icon = e.currentTarget.querySelector('.solution-icon') as HTMLElement;
                            const description = e.currentTarget.querySelector('.solution-description') as HTMLElement;
                            if (icon) icon.style.color = 'white';
                            if (description) description.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '';
                            e.currentTarget.style.color = 'black';
                            const icon = e.currentTarget.querySelector('.solution-icon') as HTMLElement;
                            const description = e.currentTarget.querySelector('.solution-description') as HTMLElement;
                            if (icon) icon.style.color = '#819A40';
                            if (description) description.style.color = '#6b7280';
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <item.icon className="h-5 w-5 solution-icon" style={{ color: '#819A40' }} />
                            <div className="text-sm font-medium leading-none">{item.label}</div>
                          </div>
                          <p className="line-clamp-2 text-xs leading-snug text-gray-600 solution-description">
                            {item.description}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Contact */}
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <button
                    onClick={() => handleNavigation('contact')}
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-white focus:text-white focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                    style={{
                      backgroundColor: activeMenu === 'contact' ? '#819A40' : 'white',
                      color: activeMenu === 'contact' ? 'white' : 'black'
                    }}
                    onMouseEnter={(e) => {
                      if (activeMenu !== 'contact') {
                        e.currentTarget.style.backgroundColor = '#819A40';
                        e.currentTarget.style.color = 'white';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeMenu !== 'contact') {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.color = 'black';
                      }
                    }}
                  >
                    Contact
                  </button>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Phone Number and CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+919830846904"
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-black hover:text-white transition-colors"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#819A40';
                e.currentTarget.style.color = 'white';
                const icon = e.currentTarget.querySelector('.phone-icon') as HTMLElement;
                if (icon) icon.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '';
                e.currentTarget.style.color = 'black';
                const icon = e.currentTarget.querySelector('.phone-icon') as HTMLElement;
                if (icon) icon.style.color = 'black';
              }}
            >
              <Phone className="h-4 w-4 phone-icon" style={{ color: 'black' }} />
              <span>+91 98308 46904</span>
            </a>
            <Button size="sm" style={{ backgroundColor: '#819A40' }} className="hover:opacity-90 text-white">
              Get a Demo
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="sm" className="text-black hover:text-white"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#819A40';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                  e.currentTarget.style.color = 'black';
                }}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <div className="flex flex-col space-y-4 mt-8">
                {/* Mobile Logo */}
                <div className="flex justify-center mb-4">
                  <img 
                    src={logoImage} 
                    alt="NexProcureAI Logo"
                    className="h-6 w-auto"
                  />
                </div>

                {/* About Us Section */}
                <div>
                  <h3 className={`mb-2 text-sm font-medium px-2 py-1 rounded ${
                    activeMenu === 'about' ? 'text-white' : 'text-gray-500'
                  }`} style={{
                    backgroundColor: activeMenu === 'about' ? '#819A40' : 'transparent'
                  }}>About Us</h3>
                  <div className="space-y-1">
                    {aboutItems.map((item) => (
                      <button
                        key={item.value}
                        onClick={() => handleNavigation(item.value)}
                        className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:text-white text-black transition-colors"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#819A40';
                          e.currentTarget.style.color = 'white';
                          const icon = e.currentTarget.querySelector('.mobile-nav-icon') as HTMLElement;
                          if (icon) icon.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                          e.currentTarget.style.color = 'black';
                          const icon = e.currentTarget.querySelector('.mobile-nav-icon') as HTMLElement;
                          if (icon) icon.style.color = '#819A40';
                        }}
                      >
                        <item.icon className="h-4 w-4 mobile-nav-icon" style={{ color: '#819A40' }} />
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Solutions Section */}
                <div>
                  <h3 className={`mb-2 text-sm font-medium px-2 py-1 rounded ${
                    activeMenu === 'solutions' ? 'text-white' : 'text-gray-500'
                  }`} style={{
                    backgroundColor: activeMenu === 'solutions' ? '#819A40' : 'transparent'
                  }}>Solutions</h3>
                  <div className="space-y-1">
                    {solutionItems.map((item) => (
                      <button
                        key={item.value}
                        onClick={() => handleNavigation(item.value)}
                        className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:text-white text-black transition-colors"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#819A40';
                          e.currentTarget.style.color = 'white';
                          const icon = e.currentTarget.querySelector('.mobile-nav-icon') as HTMLElement;
                          if (icon) icon.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                          e.currentTarget.style.color = 'black';
                          const icon = e.currentTarget.querySelector('.mobile-nav-icon') as HTMLElement;
                          if (icon) icon.style.color = '#819A40';
                        }}
                      >
                        <item.icon className="h-4 w-4 mobile-nav-icon" style={{ color: '#819A40' }} />
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact */}
                <button
                  onClick={() => handleNavigation('contact')}
                  className="flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:text-white transition-colors"
                  style={{
                    backgroundColor: activeMenu === 'contact' ? '#819A40' : 'transparent',
                    color: activeMenu === 'contact' ? 'white' : 'black'
                  }}
                  onMouseEnter={(e) => {
                    if (activeMenu !== 'contact') {
                      e.currentTarget.style.backgroundColor = '#819A40';
                      e.currentTarget.style.color = 'white';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeMenu !== 'contact') {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.color = 'black';
                    }
                  }}
                >
                  Contact
                </button>

                <div className="pt-4 space-y-3">
                  <a
                    href="tel:+919830846904"
                    className="flex items-center gap-2 w-full rounded-md px-3 py-2 text-sm font-medium text-black hover:text-white border border-gray-200 transition-colors"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#819A40';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = '#819A40';
                      const icon = e.currentTarget.querySelector('.mobile-phone-icon') as HTMLElement;
                      if (icon) icon.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                      e.currentTarget.style.color = 'black';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      const icon = e.currentTarget.querySelector('.mobile-phone-icon') as HTMLElement;
                      if (icon) icon.style.color = 'black';
                    }}
                  >
                    <Phone className="h-4 w-4 mobile-phone-icon" style={{ color: 'black' }} />
                    <span>+91 98308 46904</span>
                  </a>
                  <Button className="w-full text-white hover:opacity-90" style={{ backgroundColor: '#819A40' }}>Get a Demo</Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}